import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Spinner } from '@/src/components/ui/spinner'
import { getDrawing } from '@/src/api/drawing'
import { listOperationTypes } from '@/src/api/operation-type'
import { OperationForm } from '@/src/components/manage/forms/OperationForm'
import { useTranslation } from '@/src/i18n/client'
import { resolveLocalizedString } from '@/src/lib/i18n-utils'

export const Route = createFileRoute('/_manage/manage/drawings_/$id/operations/$operationTypeId/new')({
  component: DrawingOperationNewPage,
})

function DrawingOperationNewPage() {
  const { t, i18n } = useTranslation('operation')
  const [mode, setMode] = useState<'basic' | 'expert'>('basic')
  const locale = i18n.language || 'en'
  const navigate = useNavigate()
  const { id, operationTypeId } = useParams({
    from: '/_manage/manage/drawings_/$id/operations/$operationTypeId/new',
  })

  const { data: drawing, isLoading: isDrawingLoading, error: drawingError } = useQuery({
    queryKey: ['drawing', id],
    queryFn: () => getDrawing({ data: { id } }),
    enabled: !!id,
  })

  const { data: operationTypes = [], isLoading: isOperationTypesLoading } = useQuery({
    queryKey: ['operationTypes', 'available'],
    queryFn: () => listOperationTypes({ data: { includeDisabled: false } }),
    staleTime: 1000 * 60 * 5,
  })

  if (isDrawingLoading || isOperationTypesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (drawingError) {
    return (
      <div className="p-4 text-red-500">
        {drawingError instanceof Error ? drawingError.message : 'Failed to load drawing'}
      </div>
    )
  }

  if (!drawing) return null

  const selectedOperationType = (operationTypes as any[]).find((type) => type.id === operationTypeId)
  const selectedOperationTypeLabel = selectedOperationType
    ? resolveLocalizedString(selectedOperationType.labels, locale)
    : null

  const goBack = () => {
    navigate({
      to: '/manage/drawings/$id',
      params: { id },
      search: { tab: 'operations' },
    })
  }

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.BackButton to={`/manage/drawings/${id}?tab=operations`} />
          <AdminToolbar.Title>
            {selectedOperationTypeLabel ? `${t('addOperation')}: ${selectedOperationTypeLabel}` : t('addOperation')}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === 'basic' ? 'default' : 'outline'}
              onClick={() => setMode('basic')}
            >
              Basic
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === 'expert' ? 'default' : 'outline'}
              onClick={() => setMode('expert')}
            >
              Expert
            </Button>
          </div>
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto p-4">
          <OperationForm
            drawingId={id}
            initialOperationTypeId={operationTypeId}
            allowCustomOperationType={false}
            lockOperationType={true}
            mode={mode}
            onModeChange={setMode}
            showModeToggle={false}
            onCancel={goBack}
            onSuccess={goBack}
          />
        </div>
      </div>
    </div>
  )
}
